import React from 'react';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import {Typography, Button, Autocomplete, CircularProgress} from '@mui/material';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import Dialog from '@mui/material/Dialog';
import TextField from '@mui/material/TextField';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import moment from 'moment';
import 'moment/locale/es';
import Colors from "../../components/ui/Colors";
import FeedOutlinedIcon from '@mui/icons-material/FeedOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ClearIcon from '@mui/icons-material/Clear';
import { PuffLoader } from "react-spinners";
import { css } from "@emotion/react";
import swal from 'sweetalert';
import {closeTransportOrder, getTransportOrderResponse, getTransportOrdersPage, getTransportOrdersByDatePage, searchBatchByCode, getTransportOrdersByBatchCode, getTransportOrdersByClientName, getProductBatch} from "../../utilityFunctions/WarehouseUtil";
import { useHistory} from "react-router-dom";
import Pagination from '@mui/material/Pagination';

const cssLoader = css`
  display: block;
  margin-left: auto;
  margin-right: auto;
`;

const dateOptions = {
    day: "numeric",
    month: "numeric",
    year: "numeric",
};

let selectedOrder;
const orderClosingRequest = { id: "", lockNumbers: "" };

// Module-level vars so renderOrderList always reads current values (no stale closures)
let ordersArray;
let activeFilter = "all";
let currentPlacaFilter = '';
let currentClientFilter = '';

export default function TransportOrdersList(props) {
    const {loginUser} = props;
    const history = useHistory();

    const [loading, setLoading] = React.useState(false);
    const [filterLoading, setFilterLoading] = React.useState(false);

    const [currentPage, setCurrentPage] = React.useState(0);
    const [totalPages, setTotalPages] = React.useState(1);
    const [pageSize] = React.useState(30);

    // Controlled input state (UI only — actual filter values live in module-level vars above)
    const [selectedDateObj, setSelectedDateObj] = React.useState(null);
    const [selectedDate, setSelectedDate] = React.useState("");

    const [batchCodeInput, setBatchCodeInput] = React.useState('');
    const [batchCodeOptions, setBatchCodeOptions] = React.useState([]);
    const [selectedBatchCode, setSelectedBatchCode] = React.useState(null);
    const [searchingBatchCodes, setSearchingBatchCodes] = React.useState(false);

    const [clientInput, setClientInput] = React.useState('');
    const [placaFilter, setPlacaFilter] = React.useState('');
    const [orderIdInput, setOrderIdInput] = React.useState('');
    const [estibaIdInput, setEstibaIdInput] = React.useState('');

    const clientDebounceRef = React.useRef(null);

    React.useEffect(() => {
        orderClosingRequest.id = null;
        activeFilter = "all";
        currentPlacaFilter = '';
        currentClientFilter = '';
        fetchPage(0, "all", {});
    }, []);

    // ── data fetchers ────────────────────────────────────────────────────────

    const fetchPage = async (page, filter, params, silent = false) => {
        if (silent) setFilterLoading(true);
        else setLoading(true);

        let result;
        if (filter === "date" && params.date) {
            result = await getTransportOrdersByDatePage({ page, size: 100, date: params.date }, loginUser.jwtToken);
        } else if (filter === "batch" && params.batchCode) {
            result = await getTransportOrdersByBatchCode({ page, size: pageSize, batchCode: params.batchCode }, loginUser.jwtToken);
        } else if (filter === "client" && params.clientName) {
            result = await getTransportOrdersByClientName({ page, size: pageSize, clientName: params.clientName }, loginUser.jwtToken);
        } else {
            result = await getTransportOrdersPage({ page, size: pageSize }, loginUser.jwtToken);
        }

        if (silent) setFilterLoading(false);
        else setLoading(false);

        if (result) {
            ordersArray = result.content;
            setTotalPages(result.totalPages);
            renderOrderList(result.content);
        }
    };

    // ── render (reads module-level filter vars — always current, no stale closures) ──

    const [ordersList, setOrdersList] = React.useState("");

    const renderOrderList = (data) => {
        let source = [...(data || ordersArray || [])];
        if (!source.length && !data) return;

        // Client-side placa filter (carPlate is always in the response)
        if (currentPlacaFilter.trim()) {
            const lower = currentPlacaFilter.trim().toLowerCase();
            source = source.filter(o => o.carPlate && o.carPlate.toLowerCase().includes(lower));
        }

        // Client-side client filter (works when deliveryOrder is loaded by open-in-view)
        if (currentClientFilter.trim().length >= 2) {
            const lower = currentClientFilter.trim().toLowerCase();
            source = source.filter(o => {
                if (!o.deliveryOrder || o.deliveryOrder.length === 0) return true;
                return o.deliveryOrder.some(d => d.clientName && d.clientName.toLowerCase().includes(lower));
            });
        }

        const sorted = [...source].sort((a, b) => {
            const sc = a.status.localeCompare(b.status);
            if (sc !== 0) return sc;
            return new Date(b.date) - new Date(a.date);
        });

        setOrdersList(
            <List sx={{width: "100%", marginTop: {xs: "1rem", sm: "0.5rem"}}}>
                {sorted.map((order) => {
                    const clientNames = order.deliveryOrder && order.deliveryOrder.length > 0
                        ? [...new Set(order.deliveryOrder.map(d => d.clientName).filter(Boolean))].join(", ")
                        : "—";
                    return (
                        <ListItem key={order.id} disablePadding divider sx={{marginBottom: "1.5rem"}}>
                            <ListItemButton onClick={() => openActionBox(order)}>
                                <Grid container spacing={1}>
                                    <Grid item xs={2} sx={{display: "flex", justifyContent: "center"}}>
                                        <Typography variant="body2">{new Intl.DateTimeFormat('es-CO', dateOptions).format(new Date(order.date))}</Typography>
                                    </Grid>
                                    <Grid item xs={2} sx={{display: "flex", justifyContent: "center"}}>
                                        <Typography variant="body2" sx={{textAlign: "center"}}>{clientNames}</Typography>
                                    </Grid>
                                    <Grid item xs={2} sx={{display: "flex", justifyContent: "center"}}>
                                        <Typography variant="body2">{order.carPlate}</Typography>
                                    </Grid>
                                    <Grid item xs={2} sx={{display: "flex", justifyContent: "center"}}>
                                        <Typography variant="body2">{order.destination}</Typography>
                                    </Grid>
                                    <Grid item xs={2} sx={{display: "flex", justifyContent: "center"}}>
                                        <Typography variant="body2">{order.id}</Typography>
                                    </Grid>
                                    <Grid item xs={2} sx={{display: "flex", justifyContent: "center"}}>
                                        <Typography variant="body2">{order.status === "active" ? "Activa" : "Completada"}</Typography>
                                    </Grid>
                                </Grid>
                            </ListItemButton>
                        </ListItem>
                    );
                })}
            </List>
        );
    };

    // ── filter handlers ──────────────────────────────────────────────────────

    const handlePlacaChange = (e) => {
        currentPlacaFilter = e.target.value;
        setPlacaFilter(e.target.value);
        renderOrderList(); // immediate client-side filter
    };

    const handleClientInputChange = (e) => {
        const value = e.target.value;
        currentClientFilter = value;
        setClientInput(value);
        renderOrderList(); // immediate client-side filter on current page

        if (clientDebounceRef.current) clearTimeout(clientDebounceRef.current);

        if (!value.trim()) {
            activeFilter = "all";
            setCurrentPage(0);
            fetchPage(0, "all", {}, true);
            return;
        }

        if (value.trim().length >= 2) {
            clientDebounceRef.current = setTimeout(() => {
                activeFilter = "client";
                setCurrentPage(0);
                fetchPage(0, "client", { clientName: value.trim() }, true);
            }, 600);
        }
    };

    const changeSelectedDate = (date) => {
        setSelectedDateObj(date);
        setSelectedBatchCode(null);
        setBatchCodeInput('');
        currentClientFilter = '';
        setClientInput('');

        if (!date) {
            activeFilter = "all";
            setSelectedDate("");
            setCurrentPage(0);
            fetchPage(0, "all", {}, true);
        } else {
            const formatted = date.format('YYYYMMDD');
            setSelectedDate(formatted);
            activeFilter = "date";
            setCurrentPage(0);
            fetchPage(0, "date", { date: formatted }, true);
        }
    };

    const handleBatchCodeSearch = async (inputValue) => {
        setBatchCodeInput(inputValue);
        if (inputValue && inputValue.length >= 3) {
            setSearchingBatchCodes(true);
            const result = await searchBatchByCode({ batchCode: inputValue }, loginUser.jwtToken);
            if (result && result.batchCodes) setBatchCodeOptions(result.batchCodes);
            setSearchingBatchCodes(false);
        } else {
            setBatchCodeOptions([]);
        }
    };

    const filterByBatchCode = (batchCode) => {
        setSelectedBatchCode(batchCode);
        setSelectedDateObj(null);
        setSelectedDate("");
        currentClientFilter = '';
        setClientInput('');

        if (!batchCode) {
            activeFilter = "all";
            setCurrentPage(0);
            fetchPage(0, "all", {}, true);
            return;
        }
        activeFilter = "batch";
        setCurrentPage(0);
        fetchPage(0, "batch", { batchCode }, true);
    };

    const clearAllFilters = () => {
        setSelectedDateObj(null);
        setSelectedDate("");
        setSelectedBatchCode(null);
        setBatchCodeInput('');
        currentClientFilter = '';
        setClientInput('');
        currentPlacaFilter = '';
        setPlacaFilter('');
        setOrderIdInput('');
        setEstibaIdInput('');
        activeFilter = "all";
        setCurrentPage(0);
        fetchPage(0, "all", {}, true);
    };

    const showSingleTransport = (transport) => {
        ordersArray = [transport];
        setTotalPages(1);
        setCurrentPage(0);
        activeFilter = "all";
        renderOrderList([transport]);
    };

    const searchByOrderId = async () => {
        const idStr = orderIdInput.trim();
        if (!idStr) return;
        const id = parseInt(idStr);
        if (isNaN(id)) return;
        setFilterLoading(true);
        const transport = await getTransportOrderResponse({ id }, loginUser.jwtToken);
        setFilterLoading(false);
        if (!transport) {
            swal({ title: "No encontrada", text: `No existe la orden de transporte ${id}`, icon: "warning", buttons: "OK" });
            return;
        }
        showSingleTransport(transport);
    };

    const searchByEstibaId = async () => {
        const idStr = estibaIdInput.trim();
        if (!idStr) return;
        const id = parseInt(idStr);
        if (isNaN(id)) return;
        setFilterLoading(true);
        const batch = await getProductBatch({ id, username: loginUser.accountId }, loginUser.jwtToken);
        if (!batch || !batch.deliveryOrder || !batch.deliveryOrder.deliveryOrderTransport) {
            setFilterLoading(false);
            swal({ title: "Sin orden de despacho", text: "La estiba no está asignada a una orden de transporte", icon: "warning", buttons: "OK" });
            return;
        }
        const transportId = batch.deliveryOrder.deliveryOrderTransport.id;
        const transport = await getTransportOrderResponse({ id: transportId }, loginUser.jwtToken);
        setFilterLoading(false);
        if (!transport) {
            swal({ title: "Error", text: "No se pudo cargar la orden de transporte", icon: "error", buttons: "OK" });
            return;
        }
        showSingleTransport(transport);
    };

    // ── pagination ───────────────────────────────────────────────────────────

    const handlePageChange = async (event, value) => {
        const page = value - 1;
        setCurrentPage(page);
        const params = {};
        if (activeFilter === "date") params.date = selectedDate;
        if (activeFilter === "batch") params.batchCode = selectedBatchCode;
        if (activeFilter === "client") params.clientName = clientInput.trim();
        fetchPage(page, activeFilter, params);
    };

    // ── actions ──────────────────────────────────────────────────────────────

    const selectOrder = async () => {
        setLoading(true);
        const orderResponse = await getTransportOrderResponse({ id: selectedOrder.id }, loginUser.jwtToken);
        setLoading(false);
        if (!orderResponse) {
            swal({ title: "Ocurrio un error", text: "Inténtalo de nuevo.", icon: "error", buttons: "OK" });
            return;
        }
        history.push({ pathname: "/warehouse/transport-order", state: { order: orderResponse } });
    };

    const [actionBox, setActionsBox] = React.useState(false);
    const closeActionBox = () => setActionsBox(false);
    const openActionBox = (order) => {
        selectedOrder = order;
        orderClosingRequest.id = order.id;
        setActionsBox(true);
    };

    const completeOrder = async () => {
        closeActionBox();
        closeOrderLocksBox();
        setLoading(true);
        const orderClosed = await closeTransportOrder(orderClosingRequest, loginUser.jwtToken);
        setLoading(false);
        if (!orderClosed) {
            swal({ title: "Ocurrio un error", text: "Inténtalo de nuevo.", icon: "error", buttons: "OK" });
            return;
        }
        const params = {};
        if (activeFilter === "date") params.date = selectedDate;
        if (activeFilter === "batch") params.batchCode = selectedBatchCode;
        if (activeFilter === "client") params.clientName = clientInput.trim();
        fetchPage(currentPage, activeFilter, params, false);
        swal({ title: "Actualización exitosa", icon: "success", buttons: "OK" });
    };

    const changeOrder = () => {
        history.push({ pathname: "/warehouse/change-transport", state: { order: selectedOrder } });
    };

    const [orderLocksBox, setOrderLocksBox] = React.useState(false);
    const [orderLocks, setOrderLocks] = React.useState("");
    const closeOrderLocksBox = () => setOrderLocksBox(false);
    const orderLocksChange = (e) => {
        setOrderLocks(e.target.value);
        orderClosingRequest.lockNumbers = e.target.value;
    };

    const hasActiveFilter = selectedDateObj || selectedBatchCode || clientInput || placaFilter || orderIdInput || estibaIdInput;

    // ── JSX ──────────────────────────────────────────────────────────────────

    return (
        <Box sx={{display: "flex", justifyContent: "center", width: "100%", height: "100%"}}>
            <Box sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: {xs: "start", sm: "center"},
                width: "90%",
                height: "100%",
                textAlign: "center",
                overflowY: "scroll",
            }}>
            {loading ? <PuffLoader size={100} color={Colors.secondary} css={cssLoader} /> :
            <>
                <Grid container>

                    {/* Title */}
                    <Grid item xs={12} sx={{mt: 2, mb: 0, textAlign: "center"}}>
                        <Typography variant="h6" sx={{fontWeight: "bold", color: Colors.primary}}>
                            Órdenes de Despacho
                        </Typography>
                    </Grid>

                    {/* Filter bar */}
                    <Grid item xs={12} sx={{mt: 1, mb: 1}}>
                        <Grid container spacing={2} alignItems="flex-end" justifyContent="center">

                            {/* Client */}
                            <Grid item xs={12} sm={3} md={2}>
                                <TextField
                                    variant="standard"
                                    fullWidth
                                    label={<Typography variant="body2">Cliente</Typography>}
                                    value={clientInput}
                                    onChange={handleClientInputChange}
                                    autoComplete="off"
                                    inputProps={{ style: { fontSize: "0.85rem" } }}
                                />
                            </Grid>

                            {/* Placa */}
                            <Grid item xs={12} sm={3} md={2}>
                                <TextField
                                    variant="standard"
                                    fullWidth
                                    label={<Typography variant="body2">Placa</Typography>}
                                    value={placaFilter}
                                    onChange={handlePlacaChange}
                                    autoComplete="off"
                                    inputProps={{ style: { fontSize: "0.85rem" } }}
                                />
                            </Grid>

                            {/* Lote */}
                            <Grid item xs={12} sm={3} md={3}>
                                <Autocomplete
                                    value={selectedBatchCode}
                                    onChange={(event, newValue) => filterByBatchCode(newValue)}
                                    inputValue={batchCodeInput}
                                    onInputChange={(event, newInputValue) => handleBatchCodeSearch(newInputValue)}
                                    options={batchCodeOptions}
                                    loading={searchingBatchCodes}
                                    loadingText="Buscando lotes..."
                                    noOptionsText={batchCodeInput.length >= 3 ? "No se encontraron lotes" : "Ingrese al menos 3 caracteres"}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={<Typography variant="body2">Lote</Typography>}
                                            variant="standard"
                                            fullWidth
                                            InputProps={{
                                                ...params.InputProps,
                                                style: { fontSize: "0.85rem" },
                                                endAdornment: (
                                                    <>
                                                        {searchingBatchCodes ? <CircularProgress color="inherit" size={16} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                            </Grid>

                            {/* Fecha */}
                            <Grid item xs={12} sm={3} md={3}>
                                <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="es">
                                    <DesktopDatePicker
                                        inputFormat="DD/MM/YYYY"
                                        value={selectedDateObj}
                                        onChange={changeSelectedDate}
                                        renderInput={(params) =>
                                            <TextField
                                                {...params}
                                                variant="standard"
                                                fullWidth
                                                label={<Typography variant="body2">Fecha</Typography>}
                                                sx={{"& input": {fontSize: "0.85rem"}}}
                                            />
                                        }
                                    />
                                </LocalizationProvider>
                            </Grid>

                            {/* ID Orden */}
                            <Grid item xs={12} sm={3} md={2} sx={{display: "flex", gap: 0.5, alignItems: "flex-end"}}>
                                <TextField
                                    variant="standard"
                                    fullWidth
                                    label={<Typography variant="body2">ID Orden</Typography>}
                                    value={orderIdInput}
                                    onChange={(e) => setOrderIdInput(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === "Enter") searchByOrderId(); }}
                                    autoComplete="off"
                                    inputProps={{ inputMode: "numeric", style: { fontSize: "0.85rem" } }}
                                />
                                <Button onClick={searchByOrderId} size="small" variant="outlined" sx={{minWidth: "auto", fontSize: "0.65rem", borderColor: Colors.primary, color: Colors.primary}}>
                                    Ir
                                </Button>
                            </Grid>

                            {/* ID Estiba */}
                            <Grid item xs={12} sm={3} md={2} sx={{display: "flex", gap: 0.5, alignItems: "flex-end"}}>
                                <TextField
                                    variant="standard"
                                    fullWidth
                                    label={<Typography variant="body2">ID Estiba</Typography>}
                                    value={estibaIdInput}
                                    onChange={(e) => setEstibaIdInput(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === "Enter") searchByEstibaId(); }}
                                    autoComplete="off"
                                    inputProps={{ inputMode: "numeric", style: { fontSize: "0.85rem" } }}
                                />
                                <Button onClick={searchByEstibaId} size="small" variant="outlined" sx={{minWidth: "auto", fontSize: "0.65rem", borderColor: Colors.primary, color: Colors.primary}}>
                                    Ir
                                </Button>
                            </Grid>

                            {/* Clear */}
                            {hasActiveFilter && (
                                <Grid item xs={12} sm={12} md={1} sx={{display: "flex", justifyContent: "center"}}>
                                    <Button
                                        onClick={clearAllFilters}
                                        size="small"
                                        variant="outlined"
                                        startIcon={<ClearIcon />}
                                        sx={{fontSize: "0.75rem", whiteSpace: "nowrap"}}
                                    >
                                        Limpiar
                                    </Button>
                                </Grid>
                            )}
                        </Grid>
                    </Grid>

                    {/* Filter loading indicator */}
                    {filterLoading && (
                        <Grid item xs={12} sx={{display: "flex", justifyContent: "center", alignItems: "center", py: 1, gap: 1}}>
                            <CircularProgress size={18} sx={{color: Colors.primary}} />
                            <Typography variant="caption" sx={{color: "text.secondary"}}>Buscando...</Typography>
                        </Grid>
                    )}

                    {/* Column headers */}
                    <Grid item xs={12} sx={{mt: 1}}>
                        <Grid container spacing={1}>
                            <Grid item xs={2} sx={{display: "flex", justifyContent: "center"}}>
                                <Typography variant="body1" sx={{color: "black", fontWeight: "bold", textDecoration: "underline"}}>Fecha</Typography>
                            </Grid>
                            <Grid item xs={2} sx={{display: "flex", justifyContent: "center"}}>
                                <Typography variant="body1" sx={{color: "black", fontWeight: "bold", textDecoration: "underline"}}>Cliente</Typography>
                            </Grid>
                            <Grid item xs={2} sx={{display: "flex", justifyContent: "center"}}>
                                <Typography variant="body1" sx={{color: "black", fontWeight: "bold", textDecoration: "underline"}}>Placa</Typography>
                            </Grid>
                            <Grid item xs={2} sx={{display: "flex", justifyContent: "center"}}>
                                <Typography variant="body1" sx={{color: "black", fontWeight: "bold", textDecoration: "underline"}}>Destino</Typography>
                            </Grid>
                            <Grid item xs={2} sx={{display: "flex", justifyContent: "center"}}>
                                <Typography variant="body1" sx={{color: "black", fontWeight: "bold", textDecoration: "underline"}}>Orden</Typography>
                            </Grid>
                            <Grid item xs={2} sx={{display: "flex", justifyContent: "center"}}>
                                <Typography variant="body1" sx={{color: "black", fontWeight: "bold", textDecoration: "underline"}}>Estado</Typography>
                            </Grid>
                        </Grid>
                    </Grid>

                    {/* List */}
                    <Grid item xs={12}>{ordersList}</Grid>

                    {/* Pagination */}
                    <Grid item xs={12} sx={{display: "flex", justifyContent: "center", mt: 2, mb: 2}}>
                        <Pagination count={totalPages} page={currentPage + 1} onChange={handlePageChange} color="primary" />
                    </Grid>
                </Grid>

                {/* Action dialog */}
                <Dialog open={actionBox} onClose={closeActionBox}
                    sx={{"& .MuiDialog-container": {"& .MuiPaper-root": {width: {xs: "80%", sm: "40%", md: "30%"}}}}}
                >
                    <List sx={{width: "100%"}}>
                        <ListItem disablePadding divider sx={{mb: "0.2rem", mt: "0.5rem"}}>
                            <ListItemButton onClick={selectOrder}>
                                <Grid container>
                                    <Grid item xs={2} sx={{display: "flex", justifyContent: "center"}}>
                                        <FeedOutlinedIcon style={{fontSize: "2rem"}}/>
                                    </Grid>
                                    <Grid item xs={9} sx={{display: "flex", justifyContent: "start"}}>
                                        <Typography variant="h6">Detalle</Typography>
                                    </Grid>
                                </Grid>
                            </ListItemButton>
                        </ListItem>
                        <ListItem disablePadding divider sx={{mb: "0.2rem", mt: "0.5rem"}}>
                            <ListItemButton onClick={changeOrder}>
                                <Grid container>
                                    <Grid item xs={2} sx={{display: "flex", justifyContent: "center"}}>
                                        <AddCircleOutlineIcon style={{fontSize: "2rem"}}/>
                                    </Grid>
                                    <Grid item xs={9} sx={{display: "flex", justifyContent: "start"}}>
                                        <Typography variant="h6">Modificaciones</Typography>
                                    </Grid>
                                </Grid>
                            </ListItemButton>
                        </ListItem>
                        <ListItem disablePadding sx={{mb: "0.5rem", mt: "0.2rem"}}>
                            <ListItemButton onClick={setOrderLocksBox}>
                                <Grid container>
                                    <Grid item xs={2} sx={{display: "flex", justifyContent: "center"}}>
                                        <CheckCircleOutlinedIcon style={{fontSize: "2rem"}}/>
                                    </Grid>
                                    <Grid item xs={9} sx={{display: "flex", justifyContent: "start"}}>
                                        <Typography variant="h6">Cerrar Orden</Typography>
                                    </Grid>
                                </Grid>
                            </ListItemButton>
                        </ListItem>
                    </List>
                </Dialog>

                {/* Order locks dialog */}
                <Dialog open={orderLocksBox} onClose={closeOrderLocksBox}
                    sx={{"& .MuiDialog-container": {"& .MuiPaper-root": {width: {xs: "80%", sm: "60%", md: "60%"}, height: "30%"}}}}
                >
                    <Grid container spacing={1} sx={{height: "100%", alignItems: "center"}}>
                        <Grid item xs={12} sx={{display: "flex", justifyContent: "center", mt: "0.5rem"}}>
                            <TextField
                                sx={{width: "90%"}}
                                variant="outlined"
                                fullWidth
                                multiline
                                rows={4}
                                label={<Typography variant="body2">Precintos</Typography>}
                                value={orderLocks}
                                onChange={orderLocksChange}
                                autoComplete="off"
                            />
                        </Grid>
                        {orderLocks && <>
                            <Grid item xs={6} sx={{display: "flex", justifyContent: "center"}}>
                                <Button onClick={closeOrderLocksBox} variant="contained" sx={{backgroundColor: Colors.primary}}>Atrás</Button>
                            </Grid>
                            <Grid item xs={6} sx={{display: "flex", justifyContent: "center"}}>
                                <Button onClick={completeOrder} variant="contained" sx={{backgroundColor: Colors.primary}}>Cerrar</Button>
                            </Grid>
                        </>}
                    </Grid>
                </Dialog>
            </>
            }
            </Box>
        </Box>
    );
}
